import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';

// Mapping from the provided source
const IntensityUnitMap = {
  CarbonIntensity: 'kg CO₂e',
  WaterIntensity: 'kL',
  WasteIntensity: 'Kg',
  HazardousWasteIntensity: 'Kg',
  NonHazardousWasteIntensity: 'Kg',
  BioMedicalWasteIntensity: 'Kg',
  FuelIntensity: 'GJ',
  RenewableEnergyIntensity: 'kWh',
  ElectricityIntensity: 'kWh',
};

const THEME = {
  primary: "#3f88a7",
  primaryDark: "#2f6e8a",
  primaryLight: "#6aaec7",
  primaryFaint: "#e8f4f8",
  accent: "#f0a500",
  success: "#2ecc8b",
  danger: "#e05c5c",
  neutral: "#64748b",
  bg: "#f0f2f5",
  surface: "#ffffff",
  border: "#e2e8f0",
  text: "#1a2535",
  textMuted: "#94a3b8",
  textSub: "#64748b",
};

/** Loading skeleton shimmer */
const Shimmer = ({ width = "100%", height = 20, radius = 6 }) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
    }}
  />
);

const IntensitySummaryCard = ({
  rawData,
  selectedIntensityType,
  selectedPeriodsValue,
  selectedLocations,
  targetKpi,
  accentColor,
  moduleIcon,
}) => {
  const fmt = (v) => {
    if (v === undefined || v === null) return "—";
    if (typeof v !== "number") return v;
    const rounded = Number(v.toFixed(2));
    const isInteger = Number.isInteger(rounded);
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: 5,
    }).format(rounded);
  };

  // ── Original computation logic — completely unchanged ─────────────────
  const processedData = useMemo(() => {
    if (!rawData.length || !selectedIntensityType || !selectedPeriodsValue.length)
      return null;

    const validLocIds = selectedLocations.map(id => id.toString());
    const validMonths = selectedPeriodsValue.map(p => p.fromDate.substring(0, 7));

    const numeratorUnit = IntensityUnitMap[selectedIntensityType.value] || '';
    let denominatorUnit = '';

    if      (targetKpi.includes('Cr'))        denominatorUnit = 'Cr';
    else if (targetKpi.includes('sq. ft'))    denominatorUnit = 'sq. ft';
    else if (targetKpi.includes('Served'))    denominatorUnit = 'Patient';
    else if (targetKpi.includes('Beds'))      denominatorUnit = 'Bed';

    const finalUnit =
      numeratorUnit && denominatorUnit
        ? `${numeratorUnit}/${denominatorUnit}`
        : numeratorUnit;

    const isSumRule =
      targetKpi.includes('Revenue') || targetKpi.includes('Patient Served');

    if (isSumRule) {
      let answerSum = 0, metricValueSum = 0;
      rawData.forEach(item => {
        const month = item.fromDate.substring(0, 7);
        if (
          item.type === selectedIntensityType.value &&
          validLocIds.includes(item.sourceId?.toString()) &&
          validMonths.includes(month)
        ) {
          const ansData   = item.intensityQuestionAnswers?.find(a => a.title === targetKpi);
          answerSum      += parseFloat(ansData?.answer      || 0);
          metricValueSum += parseFloat(ansData?.metricValue || 0);
        }
      });
      return { value: answerSum > 0 ? metricValueSum / answerSum : 0, unit: finalUnit };
    }

    let metricValueSum      = 0;
    const locationPeriodMap = {};

    rawData.forEach(item => {
      const month = item.fromDate.substring(0, 7);
      if (
        item.type === selectedIntensityType.value &&
        validLocIds.includes(item.sourceId?.toString()) &&
        validMonths.includes(month)
      ) {
        const ansData     = item.intensityQuestionAnswers?.find(a => a.title === targetKpi);
        const answer      = parseFloat(ansData?.answer      || 0);
        const metricValue = parseFloat(ansData?.metricValue || 0);
        metricValueSum   += metricValue;

        if (!locationPeriodMap[item.sourceId])        locationPeriodMap[item.sourceId] = {};
        if (!locationPeriodMap[item.sourceId][month]) locationPeriodMap[item.sourceId][month]  = answer;
        else                                          locationPeriodMap[item.sourceId][month] += answer;
      }
    });

    const denominator = Object.values(locationPeriodMap).reduce((locationSum, periodMap) => {
      const periodAnswers = Object.values(periodMap);
      if (!periodAnswers.length) return locationSum;
      return locationSum + periodAnswers.reduce((a, b) => a + b, 0) / periodAnswers.length;
    }, 0);

    return { value: denominator > 0 ? metricValueSum / denominator : 0, unit: finalUnit };

  }, [rawData, selectedIntensityType, selectedPeriodsValue, selectedLocations, targetKpi]);
  // ─────────────────────────────────────────────────────────────────────

  const accent = accentColor || THEME.primary;
  const iconBg = accent + "18";

  const T = {
    title: {
      fontFamily:      "'DM Sans', system-ui, sans-serif",
      fontSize:        13,
      fontWeight:      600,
      color:           "#374151",
      letterSpacing:   "-0.01em",
      lineHeight:      1.4,
      display:         "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow:        "hidden",
    },
    heroValue: {
      fontFamily:    "'DM Sans', system-ui, sans-serif",
      fontSize:      28,
      fontWeight:    800,
      letterSpacing: "-0.035em",
      lineHeight:    1.05,
      color:         "#0f172a",
    },
    heroUnit: {
      fontFamily:    "'DM Mono', monospace",
      fontSize:      12,
      fontWeight:    600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color:         accent,
      paddingBottom: 2,
    },
    rowLabel: {
      fontFamily:    "'DM Mono', monospace",
      fontSize:      10,
      fontWeight:    600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color:         "#94a3b8",
    },
    muted: {
      fontFamily: "'DM Sans', system-ui, sans-serif",
      fontSize:   12,
      color:      "#94a3b8",
    },
  };

  return (
    <div style={{
      background:    "#ffffff",
      borderRadius:  16,
      border:        `1px solid ${THEME.border}`,
      boxShadow:     "0 1px 4px rgba(0,0,0,0.06)",
      overflow:      "hidden",
      fontFamily:    "'DM Sans', system-ui, sans-serif",
      display:       "flex",
      flexDirection: "column",
      minHeight:     150,
      width:         "100%",
      boxSizing:     "border-box",
    }}>

      {/* ── Top accent stripe ── */}
      <div style={{ height: 3, background: accent, flexShrink: 0 }} />

      {/* ── Body ── */}
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 0, flex: 1 }}>

        {/* ── ROW 1-2 : Title ── */}
        <div style={{
          display:        "flex",
          alignItems:     "flex-start",
          justifyContent: "space-between",
          gap:            8,
          marginBottom:   14,
        }}>
          <div style={{ ...T.title, flex: 1, paddingRight: moduleIcon ? 4 : 0 }}>
            {targetKpi}
          </div>
          {moduleIcon && (
            <div style={{
              flexShrink:     0,
              width:          38,
              height:         38,
              borderRadius:   10,
              background:     iconBg,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              fontSize:       18,
            }}>
              {moduleIcon}
            </div>
          )}
        </div>

        {/* ── Divider ── */}
        <div style={{
          height:       1,
          background:   `linear-gradient(90deg, ${accent}40, #f1f5f9 60%, transparent)`,
          marginBottom: 14,
          flexShrink:   0,
        }} />

        {/* ── DATA ROWS ── */}
        {!rawData ? (
          <Shimmer height={32} width="60%" />
        ) : !processedData ? (
          <span style={T.muted}>No data available</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={T.rowLabel}>
              {selectedIntensityType?.label ?? selectedIntensityType?.value ?? "Value"}
            </span>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={T.heroValue}>
                {fmt(processedData.value)}
              </span>
              {processedData.unit && (
                <span style={T.heroUnit}>
                  {processedData.unit}
                </span>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default IntensitySummaryCard;